import React, { useState } from 'react'
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react'

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field
  })

  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')
    setErrorMessage('')

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          description: formData.description,
          website_url: formData.website_url, // Mandatory honeypot field
        }),
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        })
      } else {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen.')
      }
    } catch (err) {
      console.error('Form submission error:', err)
      setStatus('error')
      setErrorMessage(err.message || 'Der opstod en netværksfejl. Kontroller din forbindelse og prøv igen.')
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8">
      {status === 'success' ? (
        <div className="text-center py-8 animate-in fade-in duration-300">
          <div className="inline-flex p-3 rounded-full bg-emerald-50 text-emerald-600 mb-4">
            <CheckCircle2 className="h-12 w-12" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Tak for din henvendelse!</h3>
          <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed">
            Vi har modtaget din besked og vil vende tilbage til dig hurtigst muligt med et uforpligtende tilbud på din maleropgave.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-bold hover:bg-gray-800 transition-colors cursor-pointer"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">Send os en besked</h3>
            <p className="text-gray-500 text-xs">
              Udfyld formularen, så kontakter vi dig til en uforpligtende snak og et godt tilbud.
            </p>
          </div>

          {/* Antispam Honeypot Field (Absolutely hidden from humans) */}
          <div className="sr-only absolute -top-9999 -left-9999" aria-hidden="true">
            <label htmlFor="website_url">Do not fill this field if you are human</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Navn <span className="text-[#ED1C26]">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Dit fulde navn"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#01529E] focus:ring-2 focus:ring-[#01529E]/10 transition-all text-sm text-gray-900 placeholder-gray-400 outline-none"
            />
          </div>

          {/* Phone & Email Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                Telefonnummer <span className="text-[#ED1C26]">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="Tlf. / Mobil"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#01529E] focus:ring-2 focus:ring-[#01529E]/10 transition-all text-sm text-gray-900 placeholder-gray-400 outline-none"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
                E-mailadresse <span className="text-[#ED1C26]">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#01529E] focus:ring-2 focus:ring-[#01529E]/10 transition-all text-sm text-gray-900 placeholder-gray-400 outline-none"
              />
            </div>
          </div>

          {/* Description Field */}
          <div>
            <label htmlFor="description" className="block text-xs font-bold text-gray-700 uppercase tracking-wider mb-1.5">
              Beskrivelse af opgaven <span className="text-[#ED1C26]">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder="Beskriv din maleropgave (f.eks. antal rum, indendørs/udendørs, sprøjtemaling af døre osv.)"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#01529E] focus:ring-2 focus:ring-[#01529E]/10 transition-all text-sm text-gray-900 placeholder-gray-400 outline-none resize-none"
            />
          </div>

          {/* Error Message */}
          {status === 'error' && (
            <div className="p-4 rounded-xl bg-[#ED1C26]/5 border border-[#ED1C26]/10 text-[#ED1C26] text-xs flex items-start gap-2.5 animate-in fade-in">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <p>{errorMessage}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="w-full py-3.5 px-6 rounded-xl bg-[#01529E] text-white font-bold text-sm shadow-md hover:bg-[#01529E]/90 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? 'Sender besked...' : 'Send besked'}
          </button>
        </form>
      )}
    </div>
  )
}
